import axios from "axios";
import { API_KEY } from "../config/appConfig";
import { Toaster } from "../shared/components/common/toaster";
import { CultureHelper } from "../helpers/cultureHelper";
import {
  getExtractedUserFromToken,
  Logout,
  getToken,
  refreshTokenIfNecessary,
  saveToken,
  setUser,
} from "./authService";
import { buildLocalizeRoute } from "../helpers/languageHelper";
import { AppRoutes } from "../constants/appRoutes";

let retry = false;

const redirectToLogin = () => {
  Logout();
  window.location.href = buildLocalizeRoute(AppRoutes.Login);
};

const axiosNew = axios.create({
  baseURL: `${API_KEY}/api/`,
  headers: {
    "Content-Type": "application/json",
  },
  // Treat only 2xx as success; all other statuses are errors that
  // will be handled by the response interceptor on a per-request basis.
  validateStatus: function (status) {
    return status >= 200 && status < 300;
  },
});

axiosNew.interceptors.request.use(async function (config) {
  config.params = {
    ...config.params,
    ...{ culture: CultureHelper.currentCulture() },
  };
  const token = getToken();
  const refreshedToken = await refreshTokenIfNecessary(token);
  config.headers.Authorization = refreshedToken
    ? `Bearer ${refreshedToken}`
    : "";
  return config;
});

axiosNew.interceptors.response.use(
  (res) => {
    if (
      res.status >= 200 &&
      res.status < 300 &&
      res.config.method !== "get" &&
      !res.config?.headers?.hide_success_toaster
    ) {
      // Toaster({ msg: 'Request Successfully Completed', isError: false });
    }
    return res;
  },
  async (error) => {
    const originalConfig = error.config;
    if (
      originalConfig?.url !== buildLocalizeRoute(AppRoutes.Login) &&
      error.response
    ) {
      if (error.response.status === 401 && !retry) {
        retry = true;
        try {
          const response = await axiosNew.post(
            "/authentication/refreshtoken",
            null,
            {
              responseType: "text",
            }
          );
          if (response.status === 401) {
            Toaster({ msg: "Session expired, Please login!" });
            redirectToLogin();
            return;
          } else if (response) {
            saveToken(response);
            const user = getExtractedUserFromToken();
            setUser(user);
            return axiosNew(originalConfig);
          }
        } catch (_err) {
          return Promise.reject(_err);
        }
      }
    }
    if (
      !error?.config?.headers?.hide_toaster &&
      !error?.response?.data?.UserLockedOut &&
      !error?.response?.data?.loginEmailNotFound
    ) {
      if (axios.isCancel(error)) {
        return;
      }

      if (error.response?.status === 403) {
        Toaster({
          msg: formatError(error?.response?.data) || "Access Denied!",
        });
      } else {
        if (
          error?.response?.data?.IbanIsNullOrEmpty ||
          error?.response?.data?.BankNameIsNullOrEmpty ||
          error?.response?.data?.insufficientFunds
        ) {
        } else {
          Toaster({
            msg:
              formatError(error?.response?.data) ||
              "Something went wrong. Please try again",
          });
        }
      }
    }
    return Promise.reject(error);
  }
);

export const axiosGet = (params, url, id, responseType = "", headers) => {
  let fullUrl = id ? `${url}/${id}` : url;
  return axiosNew.get(fullUrl, {
    params: params,
    headers: headers,
    responseType: responseType,
  });
};

export const axiosPost = (body, url, headers, responseType) => {
  return axiosNew.post(url, body, {
    headers: headers,
    responseType: responseType,
  });
};

export const axiosPut = (body, url, headers, responseType) => {
  return axiosNew.put(url, body, {
    headers: headers,
    responseType: responseType,
  });
};

export const axiosPatch = (
  url,
  body,
  headers,
  responseType,
  onUploadProgress
) => {
  return axiosNew.patch(url, body, {
    headers: headers,
    responseType: responseType,
    onUploadProgress: onUploadProgress,
  });
};

export const axiosDelete = (url, headers, responseType) => {
  return axiosNew.delete(url, {
    headers: headers,
    responseType: responseType,
  });
};

const formatErrorObject = (error) => {
  const keys = Object.keys(error);
  const errors = keys.map((k) => error[k]);
  if (errors.join("<br />") !== "[object Object]") {
    return errors.join("<br />");
  }
  if (errors.length && error[0]?.message) {
    return error[0]?.message;
  }
  return null;
};

export const formatError = (error) => {
  if (!error) {
    return "Something went wrong. Please try again";
  }

  if (typeof error === "string") {
    let parseValues = JSON.parse(error);
    if (Object.keys(parseValues).length) {
      return formatErrorObject(parseValues);
    }
    return error;
  }

  if (error?.errors && typeof error?.errors == "object") {
    return formatErrorObject(error?.errors);
  }

  if (typeof error == "object") {
    return formatErrorObject(error);
  }

  return error;
};

export { axiosNew };
